import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase, supabaseReady } from '@/lib/supabase'
import { isRealAthleteId } from './useSetLogs'
import type { Program } from '@/types'

/** DB row → app Program (template jsonb holds weeks + overview). */
interface ProgramRow {
  id: string
  name: string
  description: string | null
  goal_tags: string[] | null
  source: Program['source']
  template: { weeks?: Program['weeks']; overview?: Program['overview'] } | null
}

function rowToProgram(r: ProgramRow): Program {
  return {
    id: r.id,
    name: r.name,
    description: r.description ?? undefined,
    goal_tags: r.goal_tags ?? undefined,
    source: r.source,
    overview: r.template?.overview,
    weeks: r.template?.weeks ?? [],
  }
}

export interface TemplateSummary {
  id: string
  name: string
  description: string | null
  source: Program['source']
  weeks: number
  exercises: number
  created_at: string
}

/** All saved templates (reusable programs), newest first. Coach-only. */
export function useTemplates() {
  return useQuery({
    queryKey: ['templates'],
    enabled: supabaseReady,
    queryFn: async (): Promise<TemplateSummary[]> => {
      const { data, error } = await supabase
        .from('programs')
        .select('id,name,description,source,template,created_at')
        .eq('is_template', true)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []).map((r) => {
        const weeks = (r.template as { weeks?: unknown[] } | null)?.weeks ?? []
        const exercises = (weeks as { days?: { exercises?: unknown[] }[] }[]).reduce(
          (n, w) => n + (w.days ?? []).reduce((m, d) => m + (d.exercises?.length ?? 0), 0),
          0
        )
        return {
          id: r.id,
          name: r.name,
          description: r.description,
          source: r.source,
          weeks: weeks.length,
          exercises,
          created_at: r.created_at,
        }
      })
    },
  })
}

/** Load a single program (template) into the editable builder. */
export function useProgramById(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ['program', id],
    enabled: enabled && supabaseReady && Boolean(id),
    queryFn: async (): Promise<Program | null> => {
      const { data, error } = await supabase.from('programs').select('*').eq('id', id).maybeSingle()
      if (error) throw error
      return data ? rowToProgram(data as unknown as ProgramRow) : null
    },
  })
}

/** Save a program as a reusable template (not assigned to anyone). */
export function useSaveTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (program: Program) => {
      const { error } = await supabase.from('programs').insert({
        name: program.name,
        description: program.description ?? null,
        goal_tags: program.goal_tags ?? [],
        source: program.source ?? 'manual',
        template: { weeks: program.weeks, overview: program.overview ?? null },
        is_template: true,
      })
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  })
}

/** Update an existing program in place (e.g. edit an athlete's live program or
 *  a template). The change is reflected immediately for the assigned athlete. */
export function useUpdateProgram() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, program }: { id: string; program: Program }) => {
      const { error } = await supabase
        .from('programs')
        .update({
          name: program.name,
          description: program.description ?? null,
          goal_tags: program.goal_tags ?? [],
          template: { weeks: program.weeks, overview: program.overview ?? null },
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['program', vars.id] })
      qc.invalidateQueries({ queryKey: ['athlete-program'] })
      qc.invalidateQueries({ queryKey: ['my-program'] })
      qc.invalidateQueries({ queryKey: ['templates'] })
    },
  })
}

/** Delete a program/template by id. */
export function useDeleteProgram() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      // Deleting cascades to athlete_programs, so a program an athlete is on
      // would take their assignment (and day completions) with it. Templates are
      // copied on assign so this shouldn't happen — but refuse rather than risk it.
      const { count, error: countErr } = await supabase
        .from('athlete_programs')
        .select('id', { count: 'exact', head: true })
        .eq('program_id', id)
      if (countErr) throw countErr
      if (count) {
        throw new Error(
          `This program is assigned to ${count} athlete${count === 1 ? '' : 's'}, so it can't be deleted.`
        )
      }
      const { error } = await supabase.from('programs').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  })
}

/** The athlete's active program + current week, loaded from athlete_programs. */
export function useAthleteProgram(athleteId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['athlete-program', athleteId],
    enabled: enabled && supabaseReady && isRealAthleteId(athleteId),
    queryFn: async (): Promise<{ program: Program; currentWeek: number } | null> => {
      const { data, error } = await supabase
        .from('athlete_programs')
        .select('current_week, program:programs(*)')
        .eq('athlete_id', athleteId)
        .eq('active', true)
        .maybeSingle()
      if (error) throw error
      if (!data || !data.program) return null
      return {
        program: rowToProgram(data.program as unknown as ProgramRow),
        currentWeek: data.current_week ?? 1,
      }
    },
  })
}

export interface SaveAndAssignInput {
  program: Program
  athleteId: string
}

/**
 * Give ONE athlete their own copy of a program and make it their active one.
 * Each athlete gets a separate programs row because "Edit program" edits in
 * place — shared rows would mean editing one athlete's program edits them all.
 *
 * Order matters: the copy is created first, the old assignment is retired, then
 * the new one inserted. If that last step fails the old assignment is restored
 * and the orphan copy removed, so an athlete is never left without a program.
 */
async function assignProgram(program: Program, athleteId: string): Promise<string> {
  const { data: prog, error: pErr } = await supabase
    .from('programs')
    .insert({
      name: program.name,
      description: program.description ?? null,
      goal_tags: program.goal_tags ?? [],
      source: program.source ?? 'manual',
      template: { weeks: program.weeks, overview: program.overview ?? null },
    })
    .select('id')
    .single()
  if (pErr) throw pErr

  const { data: retired, error: rErr } = await supabase
    .from('athlete_programs')
    .update({ active: false })
    .eq('athlete_id', athleteId)
    .eq('active', true)
    .select('id')
  if (rErr) {
    await supabase.from('programs').delete().eq('id', prog.id)
    throw rErr
  }

  const firstWeek = program.weeks[0]?.week ?? 1
  const { error: aErr } = await supabase.from('athlete_programs').insert({
    athlete_id: athleteId,
    program_id: prog.id,
    current_week: firstWeek,
    active: true,
  })
  if (aErr) {
    const ids = (retired ?? []).map((r: { id: string }) => r.id)
    if (ids.length) await supabase.from('athlete_programs').update({ active: true }).in('id', ids)
    await supabase.from('programs').delete().eq('id', prog.id)
    throw aErr
  }
  return prog.id as string
}

/** Persist a program and assign it to one athlete. */
export function useSaveAndAssign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ program, athleteId }: SaveAndAssignInput) => {
      if (!supabaseReady) throw new Error('Database not configured')
      return { programId: await assignProgram(program, athleteId) }
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ['athlete-program', vars.athleteId] })
      qc.invalidateQueries({ queryKey: ['active-assignments'] })
      qc.invalidateQueries({ queryKey: ['athletes'] })
    },
  })
}

export interface AssignResult {
  athleteId: string
  name: string
  ok: boolean
  error?: string
}

/**
 * Assign a program to several athletes. Runs one athlete at a time and never
 * stops on a failure, so one bad row can't leave the rest half-done — every
 * athlete's outcome is reported back.
 */
export function useAssignToMany() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      program,
      athletes,
      onProgress,
    }: {
      program: Program
      athletes: { id: string; name: string }[]
      onProgress?: (done: number) => void
    }): Promise<AssignResult[]> => {
      if (!supabaseReady) throw new Error('Database not configured')
      const results: AssignResult[] = []
      for (const a of athletes) {
        try {
          await assignProgram(program, a.id)
          results.push({ athleteId: a.id, name: a.name, ok: true })
        } catch (e) {
          results.push({ athleteId: a.id, name: a.name, ok: false, error: (e as Error).message })
        }
        onProgress?.(results.length)
      }
      return results
    },
    onSettled: (_d, _e, vars) => {
      for (const a of vars.athletes) qc.invalidateQueries({ queryKey: ['athlete-program', a.id] })
      qc.invalidateQueries({ queryKey: ['active-assignments'] })
      qc.invalidateQueries({ queryKey: ['athletes'] })
    },
  })
}
